import { Text } from '@thanx/uikit/text'
import { Role, rolesApi } from 'api/roles'
import Select from 'components/Form/Select'
import Modal from 'components/ModalContainer'
import { RemovableChip } from 'components/RemovableChip'
import { RoleBadge } from 'components/RoleBadge'
import Section from 'components/Section'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import {
  getRoleBadgeVariant,
  isAdminRole,
  isAssignableRole,
} from 'constants/roles'
import { buildTranslate } from 'locales'
import { ReactNode, useMemo, useState } from 'react'
import { components as rsComponents } from 'react-select'
import { useStyletron } from 'styletron-react'
import { RbacRoleInfoBanner } from './RbacRoleInfoBanner'

const t = buildTranslate('merchant_users.rbac')

type RoleOption = {
  value: number
  label: string
  description: string | null
  role: Role
}

type Props = {
  selectedRoleIds: number[]
  onChange: (roleIds: number[]) => void
  locationIds: number[]
  disabled?: boolean
}

type RoleOptionComponentProps = {
  data: RoleOption
  children?: ReactNode
  [key: string]: any
}

type GroupHeadingComponentProps = {
  children?: ReactNode
  [key: string]: any
}

type SingleValueComponentProps = {
  data: RoleOption
  children?: ReactNode
  [key: string]: any
}

const CONFIRM_KEY = 'confirm_admin'

const CustomOption = (props: RoleOptionComponentProps) => {
  const { data } = props
  const badgeVariant = data.role ? getRoleBadgeVariant(data.role) : null

  return (
    <rsComponents.Option {...props}>
      <div className="py-xs">
        <div className="d-flex align-items-center">
          <Text.Header4 bold className="m-0">
            {data.label}
          </Text.Header4>
          <RoleBadge variant={badgeVariant} className="ml-xs" />
        </div>
        {data.description && (
          <Text.BodyText4 color="grey70" className="m-0">
            {data.description}
          </Text.BodyText4>
        )}
      </div>
    </rsComponents.Option>
  )
}

const CustomGroupHeading = (props: GroupHeadingComponentProps) => {
  const [css] = useStyletron()
  if (!props.children) {
    return (
      <div
        className={css({
          borderTop: `1px solid ${palette.grey20}`,
          margin: '0 16px',
        })}
      />
    )
  }
  return <rsComponents.GroupHeading {...props} />
}

const CustomSingleValue = (props: SingleValueComponentProps) => {
  const { data } = props
  return (
    <rsComponents.SingleValue {...props}>{data.label}</rsComponents.SingleValue>
  )
}

export const RbacRoleDropdown = ({
  selectedRoleIds,
  onChange,
  locationIds,
  disabled = false,
}: Props) => {
  const { data, isLoading } = rolesApi.useGetRolesQuery()
  const [pendingAddId, setPendingAddId] = useState<number | null>(null)

  const allRoles = data?.roles ?? []
  const selectedIdSet = useMemo(
    () => new Set(selectedRoleIds),
    [selectedRoleIds]
  )

  const toOption = (role: Role): RoleOption => ({
    value: role.id,
    label: role.display_name,
    description: role.description,
    role,
  })

  const availableRoles = allRoles.filter(
    role => isAssignableRole(role) && !selectedIdSet.has(role.id)
  )
  const standardOptions = availableRoles.filter(r => r.preset).map(toOption)
  const customOptions = availableRoles.filter(r => !r.preset).map(toOption)

  const groupedOptions =
    standardOptions.length > 0 && customOptions.length > 0
      ? [
          { label: '', options: standardOptions },
          { label: '', options: customOptions },
        ]
      : [...standardOptions, ...customOptions]

  const selectedRoles = selectedRoleIds
    .map(id => allRoles.find(r => r.id === id))
    .filter((r): r is Role => Boolean(r))

  const handleAdd = (option: RoleOption | null) => {
    if (!option) return

    if (isAdminRole(option.role)) {
      setPendingAddId(option.value)
      return
    }

    onChange([...selectedRoleIds, option.value])
  }

  const handleConfirm = () => {
    if (
      pendingAddId === null ||
      !availableRoles.some(role => role.id === pendingAddId)
    ) {
      setPendingAddId(null)
      return
    }
    onChange([...selectedRoleIds, pendingAddId])
    setPendingAddId(null)
  }

  const handleCancelConfirm = () => {
    setPendingAddId(null)
  }

  const handleRemove = (roleId: number) => {
    onChange(selectedRoleIds.filter(id => id !== roleId))
  }

  return (
    <Spinner isLoading={isLoading}>
      <Text.SmallCaps3 bold className="mb-xs">
        {t('role_section_header')}
      </Text.SmallCaps3>
      <Select
        options={groupedOptions}
        value={null}
        onChange={handleAdd}
        placeholder={
          availableRoles.length === 0
            ? t('no_roles_available')
            : t('role_placeholder')
        }
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          GroupHeading: CustomGroupHeading,
        }}
        styles={{
          groupHeading: () => ({ padding: 0, margin: 0 }),
          group: (base: any) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
        }}
        isSearchable={false}
        isDisabled={disabled || availableRoles.length === 0}
      />
      <RbacRoleInfoBanner roles={selectedRoles} locationIds={locationIds} />
      {selectedRoles.length > 0 && (
        <div className="mt-xl">
          <Text.SmallCaps3 bold className="mb-s">
            {t('roles_selected_section_header')}
          </Text.SmallCaps3>
          <Section className="p-l">
            <div className="mb-m">
              <Text.Header4 bold className="m-0">
                {t('roles_selected')}
              </Text.Header4>
              <Text.BodyText4 color="grey70">
                {selectedRoles.length === 1
                  ? t('roles_selected_count', { count: 1 })
                  : t('roles_selected_count_plural', {
                      count: selectedRoles.length,
                    })}
              </Text.BodyText4>
            </div>
            {/* Inline gap style used because Bootstrap gap-2 utility
                does not reliably apply in this context */}
            <div
              className="d-flex flex-wrap"
              style={{ gap: '8px' }}
              role="group"
              aria-label={t('roles_selected')}
            >
              {selectedRoles.map(role => (
                <RemovableChip
                  key={role.id}
                  label={role.display_name}
                  onRemove={disabled ? undefined : () => handleRemove(role.id)}
                  disabledTooltip={disabled ? t('requires_admin') : undefined}
                />
              ))}
            </div>
          </Section>
        </div>
      )}
      <Modal
        isOpen={pendingAddId !== null}
        onClose={handleCancelConfirm}
        onProceed={handleConfirm}
        proceedText={t(`${CONFIRM_KEY}.proceed`)}
        title={t(`${CONFIRM_KEY}.title`)}
      >
        <div className="mt-xl">
          <Text.BodyText2>{t(`${CONFIRM_KEY}.description`)}</Text.BodyText2>
        </div>
      </Modal>
    </Spinner>
  )
}
